import posthog from 'posthog-js';

/**
 * Capture an error-boundary error safely.
 *
 * The raw `error` React hands an error boundary can carry a non-serializable,
 * circular reference — observed in production as an HTMLAnchorElement whose
 * `__reactFiber$…` property points to a fiber whose `stateNode` points back to
 * the element. Passing that object straight to `posthog.captureException` makes
 * posthog-js's JSON serialization throw "Converting circular structure to JSON",
 * which posthog-js then reports as its OWN bogus TypeError (PostHog issue
 * 019f5a14) — masking the real error we were trying to record.
 *
 * So we hand PostHog a flat Error carrying only serializable fields (message,
 * name, stack) plus the Next.js `digest` as a property. Fails closed: anything
 * that throws in here is swallowed so telemetry can never break the error screen.
 */
export function captureBoundaryError(error: Error & { digest?: string }): void {
  try {
    const safe = new Error(error?.message ?? 'Unknown error');
    safe.name = error?.name ?? 'Error';
    if (error?.stack) safe.stack = error.stack;
    posthog.captureException(safe, error?.digest ? { digest: error.digest } : undefined);
  } catch {
    // never let telemetry break the error screen
  }
}
